import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["ariaCaption", "caption", "columnKeys", "customStrings", "onSortedIndicatorChange", "tableComponent", "sortedIndicator", "defaultSortDirection", "tableId"],
      _excluded2 = ["sortable", "data"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { useUniqueId } from "../../common/hooks";
import LiveRegion from "../../private/LiveRegion/LiveRegion";
import { getColumnHeaderContent, getSortableButtonAriaDescribedById } from "../utils";
import { getSortedStatusUpdates } from "./ariaUtils";
import { ANNOUNCEMENT_DELAY, SORT_ASCENDING, SORT_DESCENDING, SORT_DIRECTIONS, SORT_UNSORTED } from "./constants";
import extractTheme from "./extractTheme";
import SortableHeaderCell from "./SortableHeaderCell";
/**
 * `withSortable` is a HOC that enhances [Table](#table) with sortable column headers.
 * It only handles the UI, the actual data sorting is left to the user.
 * @type {Component}
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const Sortable = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    ariaCaption,
    caption,
    // eslint-disable-line react/prop-types
    columnKeys,
    customStrings,
    onSortedIndicatorChange,
    // eslint-disable-line no-unused-vars
    tableComponent: TableComponent,
    sortedIndicator,
    // eslint-disable-line no-unused-vars
    defaultSortDirection,
    tableId
  } = _ref,
      tableProps = _objectWithoutProperties(_ref, _excluded);

  const internalRef = useRef();
  const tableRef = ref || internalRef;
  const theme = useTheme();
  const {
    sortableStyles
  } = extractTheme({
    theme
  });
  const {
    headerCellStyles,
    headerSortedCellStyles,
    sortContainerStyle,
    bodyCellStyles
  } = sortableStyles;
  const tableUniqueId = useUniqueId("pnr-table-");
  const tableID = tableId || tableUniqueId;
  /**
   * We need to keep direction and columnKey in ref so we have
   * them to compare against when new columnKey or direction are
   * passed by in props.
   */

  const sortableInfo = useRef({
    sortDirection: sortedIndicator.direction,
    sortKey: sortedIndicator.columnKey
  });
  /**
   * @param {Array} Array of column objects
   * @param {String} String representing the key of a column
   * @returns {String} column label
   */

  const getColumnLabel = (columns, columnKey) => {
    const column = columns.find(_ref2 => {
      let {
        key
      } = _ref2;
      return key === columnKey;
    });
    return column ? getColumnHeaderContent(column) : "";
  };
  /**
   * @param {Object} Object of 2 sort properties to be transferred from `SortableHeaderCell` to parent
   * Updates sortIndicator and communicates to parent table onSort.
   */


  const handleSortedIndicatorChange = _ref3 => {
    let {
      direction,
      columnKey
    } = _ref3;
    const sortedIndicator = {
      direction,
      columnKey
    };
    onSortedIndicatorChange(sortedIndicator);
  };

  useEffect(() => {
    /**
     * When we have a change in sortable direction or columnKey,
     * we need to announce it. This is done by setting the flag `announceSorting` and
     * reading it in the render method.
     */
    const {
      direction: directionProp,
      columnKey: columnKeyProp
    } = sortedIndicator;
    sortableInfo.current = {
      sortDirection: directionProp,
      sortKey: columnKeyProp
    };
  }, [sortedIndicator]);
  const {
    columnKey,
    direction
  } = sortedIndicator;
  const {
    columns: originalColumns
  } = tableProps;
  const columns = originalColumns.map(column => {
    const {
      key,
      className
    } = column;
    const isSortable = columnKeys.length === 0 || columnKeys.indexOf(key) >= 0;

    if (isSortable) {
      const isSorted = key === columnKey;
      const cellClassName = clsx(className, {
        "pnr-table__col--sorted": isSorted
      });
      const columnDirection = isSorted ? direction : SORT_UNSORTED;
      const headerCellStyle = isSorted ? headerSortedCellStyles : headerCellStyles;
      return _objectSpread(_objectSpread({}, column), {}, {
        className: cellClassName,
        headerCellComponent: SortableHeaderCell,
        headerCellComponentProps: {
          role: "columnheader",
          css: headerCellStyle,
          sortContainerStyles: sortContainerStyle,
          onSortedIndicatorChange: handleSortedIndicatorChange,
          direction: columnDirection,
          defaultSortDirection,
          customStrings,
          buttonAriaDescribedById: getSortableButtonAriaDescribedById(tableID, columnDirection)
        },
        bodyCellComponentProps: {
          css: isSorted && bodyCellStyles
        }
      });
    }

    return column;
  });
  let modifiedAriaCaption = ariaCaption,
      columnLabel;

  if (columnKey) {
    columnLabel = getColumnLabel(columns, columnKey);
    modifiedAriaCaption = getSortedStatusUpdates(customStrings, ariaCaption, direction, columnLabel);
  }

  const {
    sorted,
    sortAscending,
    sortDescending
  } = customStrings;
  const {
    sortDirection,
    sortKey
  } = sortableInfo.current;
  const announceSorting = sortDirection !== direction || sortKey !== columnKey;
  return _jsxs(React.Fragment, {
    children: [_jsx("div", {
      id: getSortableButtonAriaDescribedById(tableID, SORT_UNSORTED),
      hidden: true,
      children: sorted
    }), _jsx("div", {
      id: getSortableButtonAriaDescribedById(tableID, SORT_ASCENDING),
      hidden: true,
      children: sortAscending
    }), _jsx("div", {
      id: getSortableButtonAriaDescribedById(tableID, SORT_DESCENDING),
      hidden: true,
      children: sortDescending
    }), columnKey && _jsx(LiveRegion, {
      clearAnnouncementDelay: ANNOUNCEMENT_DELAY,
      children: announceSorting ? getSortedStatusUpdates(customStrings, ariaCaption, direction, columnLabel, true) : null
    }), _jsx(TableComponent, _objectSpread(_objectSpread({
      ariaCaption: modifiedAriaCaption,
      caption: caption,
      customStrings: customStrings
    }, tableProps), {}, {
      css: sortableStyles,
      columns: columns,
      ref: tableRef
    }))]
  });
});
Sortable.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Override for strings used within the component
   */
  customStrings: PropTypes.shape({
    // Used for text 'ascending' in `Sortable` HOC
    sortAscending: PropTypes.string,
    // Used for text 'descending' in `Sortable` HOC
    sortDescending: PropTypes.string,
    // Used for text 'sorted" in `Sortable` HOC
    sorted: PropTypes.string
  }).isRequired,

  /**
   * Event prop called when the table sort changes. It will receive
   * the new sort criteria object
   */
  onSortedIndicatorChange: PropTypes.func.isRequired,

  /**
   * Tells the Table which column should display the sort Indicator and in which direction
   */
  sortedIndicator: PropTypes.shape({
    columnKey: PropTypes.string,
    direction: PropTypes.oneOf(SORT_DIRECTIONS)
  }).isRequired,

  /**
   * The Table component to be used as extension. Used by the High Order Component implementation
   */
  tableComponent: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Provides the option to enhance the Table caption by adding an additional accessible announcement,
   * for Assistive Technology, to the Table caption. It can also stand alone, without a Table caption,
   * offering Assistive Technology further information about the Table.
   */
  ariaCaption: PropTypes.string,

  /** Sortable configuration for the columns. If empty, all columns will be sortable */
  columnKeys: PropTypes.arrayOf(PropTypes.string),
  defaultSortDirection: PropTypes.oneOf(SORT_DIRECTIONS),

  /** Table unique id */
  tableId: PropTypes.string
} : {};
Sortable.defaultProps = {
  ariaCaption: null,
  columnKeys: [],
  customStrings: {},
  defaultSortDirection: SORT_DESCENDING,
  tableId: ""
};
/**
 * A higher-order component that will return a Sortable component
 * which will internally use the passed Table class argument as the React element
 * @param {React.Element} TableComponent The Table component or another wrapped
 *                                        Table component.
 * @return {Function}                     A Functional component that will enhance
 *                                        TableComponent
 */

function withSortable(TableComponent) {
  const WrappedTable = /*#__PURE__*/React.forwardRef((props, ref) => {
    const {
      sortable,
      data
    } = props,
          others = _objectWithoutProperties(props, _excluded2);

    if (sortable && data.length) {
      return _jsx(Sortable, _objectSpread(_objectSpread(_objectSpread({}, others), sortable), {}, {
        ref: ref,
        data: data,
        tableComponent: TableComponent
      }));
    } else {
      return _jsx(TableComponent, _objectSpread({
        ref: ref,
        data: data
      }, others));
    }
  });
  WrappedTable.displayName = `withSortable(${TableComponent.displayName || TableComponent.name})`;
  return WrappedTable;
}

export default withSortable;